import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Typography,
  Avatar,
  Box,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  Button,
  Tooltip,
  IconButton,
  TextField,
  CircularProgress,
  Modal
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useAuth } from '../../contexts/authContext';
import { useNavigate } from 'react-router-dom';
import SnackbarComponent from '../../utils/SnackbarComponent';
import { getWebAppUrl } from '../../config/runtimeConfig';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Close } from '@mui/icons-material';
import { getUserProfileWithBadges, updateProfileId, getUserContext } from '../../services/apiservice';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getSelectedRole } from '../../utils/auth';
import { useApiData } from '../../hooks/useApi';

const BadgeProfile = () => {
  const { user, setUser } = useAuth();
  const ProfileID = user?.ProfileID;
  const [userProfile, setUserProfile] = useState({ badges: [] });
  const [sortOrder, setSortOrder] = useState('Most Recent');
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newProfileID, setNewProfileID] = useState(ProfileID);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const { data: fetchedProfile, loading: isProfileLoading } = useApiData(() => getUserProfileWithBadges(ProfileID).then((res) => res.data), [ProfileID, user, setUser]);
  const [isLoading, setIsLoading] = useState(false);
  const [profileImageMissing, setProfileImageMissing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const allowedRoles = ['Admin', 'User'];
  const email = allowedRoles.includes(user.role) ? user.UserEmail : user.IssuerRepEmailID;
  // const email = 
  // user.role === "User" && user.IssuerRepEmailID 
  //   ? user.IssuerRepEmailID 
  //   : user.UserEmail;
  const selectedRole = getSelectedRole()?.selectedRole || null;
  const profileLink = `${getWebAppUrl()}/users/${userProfile.ProfileID}`;

  useEffect(() => {
    if (fetchedProfile) {
      setUserProfile(fetchedProfile);
    }
  }, [fetchedProfile]);

  useEffect(() => {
    if (user) {
      const isProfileComplete = user.FullName !== null && user.UserPhoneNumber !== null && user.UserJobTitle !== null && user.UserGender !== null;
      if (isProfileComplete) {
        setModalOpen(false);
      } else {
        setModalOpen(true);
      }
    } else {
      console.warn("User object is undefined");
    }
  }, [user]);

  const handleModalClose = () => {
    setModalOpen(false);
    navigate('/user/profile');
  };

  const handleProfileIDUpdate = async () => {
    if (!newProfileID.trim()) {
      setSnackbarMessage('ProfileID cannot be empty. Please enter a valid ProfileID.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setIsEditing(true);
      return;
    }

    if (newProfileID.trim() === userProfile.ProfileID) {
      setSnackbarMessage('No changes detected. Please update the ProfileID before saving.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setIsEditing(false);
      return;
    }

    if (newProfileID.trim()) {
      setIsLoading(true);
      try {
        const response = await updateProfileId(userProfile.ProfileID, newProfileID);
        if (response.success) {
          setUserProfile((prevProfile) => ({
            ...prevProfile,
            ProfileID: newProfileID,
          }));
          setNewProfileID(newProfileID);
          const userContextResponse = await getUserContext(email, selectedRole);
          if (userContextResponse.success) {
            setUser({ ...userContextResponse.data.userContext, role: selectedRole });
            setSnackbarMessage('ProfileID Updated successfully!');
            setSnackbarSeverity('success');
          } else {
            setSnackbarSeverity('error');
            setSnackbarMessage('Error fetching updated user context');
          }
          setIsEditing(false);
        } else {
          console.error('Failed to update ProfileID');
          setSnackbarMessage('Failed to update ProfileID');
          setSnackbarSeverity('error');
        }
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error updating ProfileID:', error);
        const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const activeBadges = userProfile.badges.filter(badge => badge.AssignmentStatus === 'Active');
  const badgeCount = activeBadges.length;

  const sortedBadges = [...activeBadges].sort((a, b) => {
    if (sortOrder === 'Most Recent') {
      return new Date(b.AssignmentDate) - new Date(a.AssignmentDate);
    }
    return b.PopularityMetric - a.PopularityMetric;
  });

  const handlePublishOnLinkedIn = () => {
    const linkedInShareURL = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileLink)}`;
    window.open(linkedInShareURL, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options).replace(/\//g, '-');
  };

  const handleCopyLink = () => {
    const link = `${getWebAppUrl()}/users/${userProfile.ProfileID}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderSkeleton = () => (
    <>
      <Box
        textAlign="center"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Skeleton variant="circular" width={120} height={120} />
        <Skeleton variant="text" height={60} width="25%" />
        <Skeleton variant="text" height={30} width="20%" />

        <Box sx={{ marginTop: 2, marginLeft: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Skeleton variant="rectangular" width={500} height={40} />
          <Box sx={{ marginLeft: '10px', height: '40px', display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="rectangular" width={100} height={40} />
          </Box>
          <Box sx={{ marginLeft: '10px', height: '40px', display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="rectangular" width={100} height={40} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <Skeleton variant="rectangular" width={280} height={40} />
        </Box>


        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          mt={2}
          mb={3}
        >
          <Skeleton variant="text" height={40} width="15%" />
          <Skeleton variant="rectangular" width={120} height={40} />
        </Box>

        <Grid container spacing={2} justifyContent="center">
          {Array.from(new Array(3)).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'left',
                  height: '230px',
                  overflow: 'hidden',
                }}
              >
                <Skeleton variant="circle" width={80} height={80} sx={{ marginRight: '10px' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="60%" height={30} />
                  <Timeline>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Box>
                          <Skeleton variant="text" width="80%" />
                          <Skeleton variant="text" width="60%" />
                        </Box>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Box>
                          <Skeleton variant="text" width="60%" />
                          <Skeleton variant="rectangular" width={60} height={24} />
                        </Box>
                      </TimelineContent>
                    </TimelineItem>
                  </Timeline>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );

  return (
    <Container
      maxWidth={false}
      sx={{
        padding: '20px',
        // background: 'linear-gradient(to right, #c3cfe2, #f5f7fa)', 
        // background: 'linear-gradient(to bottom, #4B0082 50%, white 50%)'
        textAlign: 'center',
        flex: 1,
      }}
    >
      <Helmet>
        <link rel="canonical" href={profileLink} />
        <title>Badge Profile</title>
      </Helmet>
      {isProfileLoading ? (
        <React.Fragment>{renderSkeleton()}</React.Fragment>
      ) : (
        <>

          <Box
            textAlign="center"
            marginBottom={4}
            sx={{
              padding: '16px',
              borderRadius: '8px',
              background: 'linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(169, 169, 169, 0.6), rgba(0, 0, 0, 0.8))', // Gradient from black to gray
              boxShadow: 3
            }}
          >
            <Avatar
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto',
                border: '4px solid rgba(255, 255, 255, 0.8)',
                boxShadow: 2
              }}
              src={userProfile.ProfileImageUrl}
              alt={userProfile.name}
            />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
              {userProfile.name}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {userProfile.UserJobTitle}
            </Typography>
          </Box>

          <Box sx={{ marginTop: 2, marginLeft: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {isEditing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff', height: '40px' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', padding: '8px', borderRight: '1px solid #ddd', minWidth: '180px' }}>
                  {`${getWebAppUrl()}/users/`}
                </Typography>
                <TextField
                  value={newProfileID}
                  onChange={(e) => setNewProfileID(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: '300px', height: '40px', padding: '0', margin: 0 }}
                  onBlur={(e) => {
                    const trimmedNewProfileID = newProfileID.trim();
                    if (trimmedNewProfileID === userProfile.ProfileID || !trimmedNewProfileID) {
                      setIsEditing(false);
                    }
                  }}
                  autoFocus
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '8px', padding: '8px 12px', backgroundColor: '#fff', minWidth: '300px', height: '40px' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', marginRight: '10px' }}>
                  {`${getWebAppUrl()}/users/${userProfile.ProfileID}`}
                </Typography>
              </Box>
            )}

            <Box sx={{ marginLeft: '10px', height: '40px', display: 'flex', alignItems: 'center' }}>
              {!copied ? (
                <Tooltip title="Copy Profile Link" arrow>
                  <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyLink} sx={{ whiteSpace: 'nowrap', height: '100%' }}>
                    Copy
                  </Button>
                </Tooltip>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <CheckCircleIcon sx={{ color: 'green', marginRight: '4px' }} />
                  <Typography variant="body2" sx={{ color: 'green' }}>Copied!</Typography>
                </Box>
              )}
            </Box>

            <Tooltip title="Edit ProfileURL" arrow>
              <Button
                onClick={isEditing ? handleProfileIDUpdate : () => setIsEditing(!isEditing)}
                variant="outlined"
                sx={{
                  marginLeft: '10px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                disabled={isEditing && (newProfileID.trim() === '' || newProfileID.trim() === userProfile.ProfileID)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isEditing ? (
                    isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <SaveIcon />
                    )
                  ) : (
                    <EditIcon />
                  )}
                  <Typography variant="body2" sx={{ marginLeft: 1 }}>
                    {isEditing ? 'Save' : 'Edit'}
                  </Typography>
                </Box>
              </Button>
            </Tooltip>
          </Box>

          <Box textAlign="center" marginTop={2}>
            {badgeCount !== 0 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handlePublishOnLinkedIn}
                startIcon={<LinkedInIcon />}
              >
                Publish Badge Profile on LinkedIn
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={handlePublishOnLinkedIn}
                startIcon={<LinkedInIcon />}
                disabled
              >
                No Badges Yet - Publish Profile on LinkedIn
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <Typography variant="h6" sx={{ textAlign: 'left', color: '#333' }}>
              {badgeCount} Badge{badgeCount !== 1 ? 's' : ''} Issued
            </Typography>
            <FormControl variant="outlined" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="Most Recent">Most Recent</MenuItem>
                <MenuItem value="Most Popular">Most Popular</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={2} justifyContent="center">
            {sortedBadges.map((badge, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '16px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(145deg, #f5f7fa, #c3cfe2)',
                    display: 'flex',
                    alignItems: 'center',
                    textAlign: 'left',
                    height: '230px',
                    overflow: 'hidden',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <Avatar
                    src={badge.CredentialImageURL}
                    alt={badge.CredentialName}
                    sx={{ width: 80, height: 80, marginRight: '10px', marginTop: '20px' }}
                  />

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'bold',
                        marginTop: 5,
                        marginLeft: 0,
                        textAlign: 'left',
                      }}
                    >
                      {badge.CredentialName}
                    </Typography>
                    <Timeline
                      sx={{
                        [`& .${timelineItemClasses.root}:before`]: {
                          flex: 0,
                          padding: 0,
                        },
                      }}
                    >
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot color="primary" />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'black', display: 'block' }}>
                              Issued on
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#777', display: 'block' }}>
                              {formatDate(badge.AssignmentDate)}
                            </Typography>
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot color="primary" />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Box>
                            <Typography variant="caption" sx={{ display: 'block', color: 'black' }}>
                              Status
                            </Typography>
                            <Chip
                              label={badge.AssignmentStatus}
                              size="small"
                              sx={{
                                backgroundColor: badge.AssignmentStatus === 'Active' ? 'green' : 'red',
                                color: 'white',
                                marginTop: '4px',
                              }}
                            />
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                    </Timeline>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <Modal
        open={modalOpen}
        onClose={() => { }}
        BackdropProps={{
          style: { backdropFilter: 'blur(4px)' }
        }}
      >
        <Box sx={{
          bgcolor: 'white',
          padding: 4,
          borderRadius: 2,
          boxShadow: 24,
          width: 480,
          mx: 'auto',
          mt: '20%',
          textAlign: 'center',
          position: 'relative',
        }}>
          <IconButton
            onClick={handleModalClose}
            sx={{ position: 'absolute', right: 16, top: 6, zIndex: 10 }}
          >
            <Close sx={{ color: 'black' }} />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            Incomplete Profile
          </Typography>
          {/* {profileImageMissing ? (
                    <Typography variant="body2" gutterBottom sx={{ color: 'red' }}>
                      Please upload a profile image.
                    </Typography>
                  ) :
                  ( */}
          <Typography variant="body2" gutterBottom>
            Please complete your profile before accessing the dashboard.
          </Typography>
          {/* )} */}
          <Button variant="contained" onClick={handleModalClose} sx={{ mt: 2 }}>
            Go to Profile
          </Button>
        </Box>
      </Modal>
    </Container>
  );
};

export default BadgeProfile;
